import { Fragment } from 'react'
import { Helmet } from 'react-helmet-async'
import { Box, Container, Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import Background from '../resources/images/background.png'

const CreatingMarketplaceListingDisclaimer = () => {
  const theme = useTheme()

  return (
    <Fragment>
      <Helmet>
        <title>
          Creating Marketplace Listing Disclaimer |{' '}
          {process.env.REACT_APP_APP_NAME}
        </title>
      </Helmet>
      <Box
        sx={{
          backgroundImage: `url(${Background})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          minHeight: '100vh',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ py: 5, color: 'common.white' }}>
            <Typography variant="body1" mt>
              Welcome to Pitchblu (the{' '}
              <span style={{ fontWeight: 700, marginLeft: '4px' }}>
                "Platform"
              </span>
              ). We recommend that you review these Disclaimers together with
              our Privacy Policy, accessible at{' '}
              <Link
                to="/privacy-policy"
                style={{
                  fontWeight: 600,
                  textDecoration: 'underline',
                  color: theme.palette.primary.main,
                }}
                target="_blank"
              >
                {`${window.location.origin}/privacy-policy`}
              </Link>{' '}
              (
              <span
                style={{
                  fontWeight: 700,
                }}
              >
                "Privacy Policy"
              </span>
              ), and our Terms of Service, available at{' '}
              <Link
                to="/terms-and-conditions"
                style={{
                  fontWeight: 600,
                  textDecoration: 'underline',
                  color: theme.palette.primary.main,
                }}
                target="_blank"
              >
                {`${window.location.origin}/terms-and-conditions`}
              </Link>{' '}
              (
              <span
                style={{
                  fontWeight: 700,
                }}
              >
                "Terms of Service"
              </span>
              ) (these Disclaimers together with the Terms of Service and
              Privacy Policy, may collectively be referred to as{' '}
              <span
                style={{
                  fontWeight: 700,
                }}
              >
                "Platform Policies"
              </span>
              ). The Privacy Policy and Disclaimers form an integral part of the
              Terms of Service and should be read together for a complete
              understanding of your rights and obligations. The terms "We,"
              "Us," and "Our" (including their lowercase variants: "we," "us,"
              and "our") shall be deemed to include and refer to
              Pitchblu/Platform, and may be used interchangeably with one
              another and the term "You", "Your' (including their lowercase
              variants: "you" and "your") may be used interchangeably with the
              term "User(s)
            </Typography>
            <Typography variant="body1" mt>
              Unless expressly stated otherwise, all terms, definitions, and
              expressions used in any of the Platform Policies shall have the
              same meaning and apply{' '}
              <span style={{ fontWeight: 700, fontStyle: 'italic' }}>
                *mutatis mutandis*
              </span>{' '}
              to this Disclaimer and vice versa, ensuring consistency across all
              such documents.
            </Typography>
            <Typography
              variant="h1"
              color="primary.main"
              textAlign="center"
              sx={{ textDecoration: 'underline', mt: 2 }}
            >
              DISCLAIMER - CREATING MARKETPLACE LISTINGS
            </Typography>
            <Typography variant="body1" mt={3}>
              Welcome to Pitchblu (the{' '}
              <span style={{ fontWeight: 700, marginLeft: '4px' }}>
                "Platform"
              </span>
              ). We are pleased to provide you with a feature to create and
              share Marketplace Listings (as defined herein) that may assist in
              facilitating professional and business interactions for the sale,
              rental, or exchange of film-related goods, equipment, props,
              costumes, or services for creative and production purposes.
              However, it is essential to understand and adhere to the following
              guidelines before creating a Marketplace Listing to ensure a safe,
              lawful, and transparent environment for all User(s).
            </Typography>
            <Typography variant="body1" mt>
              {' '}
              ("
              <span
                style={{
                  fontWeight: 700,
                }}
              >
                Users
              </span>
              " refers to individuals who access and utilize the Platform for
              the purpose of engaging in communication, networking, and
              establishing business or professional relationships with other
              individuals on the Platform)
            </Typography>
            <Typography variant="body1" mt>
              Prior to creating a Marketplace Listing on the Platform, please
              carefully review the following disclaimer:
            </Typography>
            <Typography
              variant="h4"
              fontWeight={700}
              mt={2}
              color="primary.main"
            >
              1. Definition of Marketplace Listings:
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              “Marketplace Listings” means any Content submitted, uploaded, or
              shared by Users on the Platform for the purpose of listing,
              advertising, offering, selling, renting, or providing information
              about film-related goods, equipment, props, costumes, tools,
              materials, or services intended for creative, filming,
              photography, or production-related use.
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              Marketplace Listings are entirely User-generated. Pitchblu does
              not own, manufacture, store, inspect, manage, control, verify,
              endorse, or guarantee any goods or services listed on the
              Marketplace.
            </Typography>
            <Typography
              variant="h4"
              fontWeight={700}
              mt={2}
              color="primary.main"
            >
              2. Prohibited Content:
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              You acknowledge and agree that the following are strictly
              prohibited from being listed on the Platform:
            </Typography>
            <Box ml={5} mt>
              <ul>
                <li>
                  <Typography variant="body1" mt textAlign="justify">
                    <span style={{ fontWeight: 700 }}>
                      Unauthorized Items or Services:
                    </span>{' '}
                    Any goods or services that you do not legally own, control,
                    or have lawful authorization to sell, rent, or provide.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" mt textAlign="justify">
                    <span style={{ fontWeight: 700 }}>
                      Illegal, Restricted, or Unsafe Items:
                    </span>{' '}
                    Items or services that are unlawful, unsafe, hazardous,
                    restricted, counterfeit, stolen, prohibited, or otherwise
                    not permitted under applicable laws or regulations.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" mt textAlign="justify">
                    <span style={{ fontWeight: 700 }}>
                      False or Misleading Listings:
                    </span>{' '}
                    Listings containing inaccurate, fraudulent, misleading, or
                    deceptive information, including misrepresentation of
                    condition, functionality, pricing, availability, or
                    ownership.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" mt textAlign="justify">
                    <span style={{ fontWeight: 700 }}>
                      Prohibited or Offensive Content:
                    </span>{' '}
                    Listings that include obscenity, offensive material,
                    discriminatory remarks, or content that violates applicable
                    laws, public policy, or community standards.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" mt textAlign="justify">
                    <span style={{ fontWeight: 700 }}>
                      Unauthorized Media or Intellectual Property:
                    </span>{' '}
                    Images, videos, descriptions, trademarks, or materials for
                    which you do not hold valid intellectual property rights,
                    licenses, or permissions.
                  </Typography>
                </li>
              </ul>
            </Box>
            <Typography variant="body1" mt textAlign="justify">
              Any breach of this clause may result in removal of the listing,
              suspension of your account, or termination of access to the
              Platform, at the sole discretion of Pitchblu. Pitchblu reserves
              the right to take appropriate legal action in the event of
              violations.
            </Typography>

            <Typography
              variant="h4"
              fontWeight={700}
              mt={2}
              color="primary.main"
            >
              3. User Responsibility:
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              By creating a Marketplace Listing, you represent and warrant that:
            </Typography>
            <Box ml={5} mt>
              <ul>
                <li>
                  <Typography variant="body1" mt textAlign="justify">
                    You are the lawful owner of the listed item or are duly
                    authorized to sell, rent, or provide the listed goods or
                    services.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" mt textAlign="justify">
                    All information provided in the listing (including
                    descriptions, pricing, images, availability, and terms) is
                    accurate, complete, lawful, and not misleading.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" mt textAlign="justify">
                    The listed goods or services comply with all applicable
                    laws, safety standards, and regulatory requirements.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" mt textAlign="justify">
                    You are solely responsible for determining and complying
                    with tax, regulatory, consumer protection, import/export,
                    and other legal obligations arising from the listing.
                  </Typography>
                </li>
              </ul>
            </Box>
            <Typography variant="body1" mt textAlign="justify">
              Any communications, negotiations, payments, deliveries, pickups,
              service fulfillment, or disputes arising from a Marketplace
              Listing are strictly between the involved Users. Pitchblu is not a
              party to such dealings and bears no responsibility or liability in
              relation thereto.
            </Typography>

            <Typography
              variant="h4"
              fontWeight={700}
              mt={2}
              color="primary.main"
            >
              4. Limitation of Liability:
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              You acknowledge and agree that Pitchblu shall bear no
              responsibility or liability, to the maximum extent permissible by
              law, for any consequences arising from your Marketplace Listing or
              from the use, purchase, rental, or reliance upon such listing by
              other Users.
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              This includes, but is not limited to, claims relating to defective
              or unsafe goods, service failures, personal injury, property
              damage, financial loss, fraud, misrepresentation, delivery issues,
              non-performance, disputes, reputational harm, or regulatory
              non-compliance.
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              Pitchblu does not verify, inspect, or endorse any Marketplace
              Listing and provides all listings strictly on an “as is” and “as
              available” basis. Users are solely responsible for independently
              verifying the legality, safety, condition, and suitability of any
              listed item or service.
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              Pitchblu may, at its sole discretion, review, flag, restrict, or
              remove listings, but assumes no obligation to do so.
            </Typography>

            <Typography
              variant="h4"
              fontWeight={700}
              mt={2}
              color="primary.main"
            >
              5. Platform Rights:
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              You hereby acknowledge and agree that Pitchblu reserves the right
              to manage, modify, restrict, conceal, suspend, or remove any
              Marketplace Listing or related Content uploaded on the Platform at
              its discretion.
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              This includes, but is not limited to, actions taken in response to
              reports of unsafe, fraudulent, misleading, illegal, or
              non-compliant listings, or where Pitchblu reasonably believes that
              continued display of such listings may expose the Platform or
              Users to risk.
            </Typography>

            <Typography
              variant="h4"
              fontWeight={700}
              mt={2}
              color="primary.main"
            >
              6. Changes to Disclaimer:
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              Pitchblu reserves the right to modify or update this disclaimer at
              any time without prior notice. Users are encouraged to review this
              disclaimer periodically to remain informed of any changes.
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              By choosing to create a Marketplace Listing and/or by accessing or
              using the Platform, you acknowledge that you have read,
              understood, and agreed to abide by the terms of this disclaimer,
              the Terms of Service, and the Privacy Policy accessible at{' '}
              <Link
                to="/privacy-policy"
                style={{
                  fontWeight: 600,
                  textDecoration: 'underline',
                  color: theme.palette.primary.main,
                }}
                target="_blank"
              >
                {`${window.location.origin}/privacy-policy`}
              </Link>{' '}
              and{' '}
              <Link
                to="/terms-and-conditions"
                style={{
                  fontWeight: 600,
                  textDecoration: 'underline',
                  color: theme.palette.primary.main,
                }}
                target="_blank"
              >
                {`${window.location.origin}/terms-and-conditions`}
              </Link>
              . If you do not agree with these terms, you are advised to refrain
              from using this feature.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Fragment>
  )
}

export default CreatingMarketplaceListingDisclaimer
